/**
 * "Dirty" (TMM-B5, FRM-008): the form's values differ from the values it opened with. An editor
 * passes the object of everything a person can change; the first render's copy (once `ready`) is
 * the baseline, so a form edited and then put back is clean.
 *
 * Sameness is by meaning, not by keystroke: strings compare trimmed, and `undefined`, `null` and
 * an empty or blank string are the same "nothing"; objects and arrays compare member by member
 * (an array is ordered); numbers, booleans and everything else compare by value.
 */
import { useState } from 'react';

function isNothing(value: unknown): boolean {
  return (
    value === undefined || value === null || (typeof value === 'string' && value.trim() === '')
  );
}

export function sameValues(a: unknown, b: unknown): boolean {
  if (isNothing(a) || isNothing(b)) return isNothing(a) && isNothing(b);
  if (typeof a === 'string' && typeof b === 'string') return a.trim() === b.trim();
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, index) => sameValues(item, b[index]))
    );
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const left = a as Record<string, unknown>;
    const right = b as Record<string, unknown>;
    const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
    for (const key of keys) {
      if (!sameValues(left[key], right[key])) return false;
    }
    return true;
  }
  return Object.is(a, b);
}

export interface DirtyState {
  dirty: boolean;
}

/**
 * `values` is every editable value of the form. `ready` is false while the initial values are
 * still loading (an edit form fetching the row): the baseline is the first values seen once
 * ready, and the form is clean until then.
 */
export function useDirty(values: unknown, ready = true): DirtyState {
  const [baseline, setBaseline] = useState<{ values: unknown } | null>(ready ? { values } : null);
  if (ready && baseline === null) setBaseline({ values });
  return { dirty: baseline !== null && !sameValues(baseline.values, values) };
}
